import { useEffect, useRef, useState, useMemo } from 'react';
import { LuMoreHorizontal, LuTrash2 } from 'react-icons/lu';
import { PiShareFatDuotone } from 'react-icons/pi';
import { TbMessageCircle } from 'react-icons/tb';
import LazyLoad from 'react-lazyload';
import { Link, useParams } from 'react-router-dom';
import { getPostById } from '../../api-services/feeds/useGetFeedById';
import { SITELINK } from '../../app-routes/Links';
import { generateRandomHexColor, getFirstLetters, getUser } from '../../app-utils';
import { Verify } from '../../assets/custom-icons';
import { useFeedComments } from '../../api-services/feeds/useGetCommentsByFeedId';
import { usePostComment } from '../../api-services/feeds/useCommentByFeedId';
import { useDeleteComment } from '../../api-services/feeds/useDeleteComment'; // Import delete hook
import { RiSendPlane2Fill } from 'react-icons/ri';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import LinkPreview from '../../components/LinkPreview';
import TopBar from '../../layouts/~includes/TopBar';
import { PostInterface } from '../../app-schemas/posts/post.schema';
import { useVoteByIndex } from '../../api-services/feeds/useVote';
import { useTheme } from '../../context/theme-context';
import { SkeletonFeed } from '../../components/skeletons';
import { createPortal } from 'react-dom';

// ─── Date formatter ───────────────────────────────────────────────────────────
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Delete Confirmation Modal Component ───────────────────────────────────────
const DeleteConfirmModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  isSubmitting 
}: { 
  isOpen: boolean; 
  onConfirm: () => void; 
  onCancel: () => void; 
  isSubmitting: boolean;
}) => {
  const { isDarkMode } = useTheme();
  
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className={`rounded-lg p-6 max-w-sm mx-4 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        <h3 className={`text-base font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delete Comment</h3>
        <p className={`text-xs mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Are you sure you want to delete this comment? This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Post Type Config ─────────────────────────────────────────────────────────
const POST_TYPE_CONFIG: Record<string, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  job: {
    label: 'Job',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-600 dark:text-blue-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  market: {
    label: 'Market',
    bg: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-600 dark:text-green-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
  },
  events: {
    label: 'Event',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-600 dark:text-purple-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  poll: {
    label: 'Poll',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-600 dark:text-yellow-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  task: {
    label: 'Task',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-600 dark:text-orange-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  rent: {
    label: 'Rent',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    text: 'text-rose-600 dark:text-rose-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  rides: {
    label: 'Ride',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    text: 'text-cyan-600 dark:text-cyan-400',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
  },
};

// ─── Type-specific detail sections ───────────────────────────────────────────
const JobDetails = ({ post }: { post: any }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-blue-900/50 bg-blue-900/20' : 'border-blue-100 bg-blue-50/50'} p-3 space-y-1.5 text-[13px]`}>
      {post.title && <p className={`font-semibold text-[15px] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{post.title}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
        {post.location && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {post.location}
          </span>
        )}
        {post.amount > 0 && (
          <span className="flex items-center gap-1 font-medium text-blue-700 dark:text-blue-400">
            {post.currency}{post.amount.toLocaleString()}{post.frequency ? `/${post.frequency}` : ''}
          </span>
        )}
        {post.start_date && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Starts {formatDate(post.start_date)}
          </span>
        )}
        {post.deadline_date && (
          <span className="text-red-500 dark:text-red-400 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Deadline: {formatDate(post.deadline_date)}
          </span>
        )}
      </div>
    </div>
  );
};

const MarketDetails = ({ post }: { post: any }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-green-900/50 bg-green-900/20' : 'border-green-100 bg-green-50/50'} p-3 text-[13px]`}>
      {post.title && <p className={`font-semibold text-[15px] mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{post.title}</p>}
      {post.amount > 0 && (
        <span className={`inline-flex items-center gap-1 font-semibold px-2.5 py-1 rounded-full text-[13px] ${isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {post.currency}{post.amount.toLocaleString()}
        </span>
      )}
    </div>
  );
};

const EventDetails = ({ post }: { post: any }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-purple-900/50 bg-purple-900/20' : 'border-purple-100 bg-purple-50/50'} p-3 space-y-1.5 text-[13px]`}>
      {post.title && <p className={`font-semibold text-[15px] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{post.title}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
        {post.location && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {post.location}
          </span>
        )}
        {post.start_date && (
          <span className="flex items-center gap-1 text-purple-700 dark:text-purple-400 font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formatDate(post.start_date)}
          </span>
        )}
        {post.deadline_date && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Ends {formatDate(post.deadline_date)}
          </span>
        )}
      </div>
    </div>
  );
};

const PollDetails = ({ post, onVoteSuccess }: { post: PostInterface; onVoteSuccess: () => void }) => {
  const { isDarkMode } = useTheme();
  const { mutate: vote, isPending } = useVoteByIndex();

  const displayOptions = useMemo(() => {
    if (post.poll_results && post.poll_results.length > 0) {
      return post.poll_results;
    }
    return (post.poll_options ?? []).map((label) => ({
      label,
      votes: 0,
      has_voted: false,
    }));
  }, [post.poll_results, post.poll_options]);

  const totalVotes = useMemo(
    () => displayOptions.reduce((a, b) => a + b.votes, 0),
    [displayOptions]
  );

  const hasVoted = useMemo(
    () => displayOptions.some((opt) => opt.has_voted),
    [displayOptions]
  );

  const maxVotes = useMemo(
    () => Math.max(...displayOptions.map((o) => o.votes), 0),
    [displayOptions]
  );

  const handleVote = (index: number) => {
    if (isPending) return;
    vote(
      { postId: post.post_id, index },
      { onSuccess: onVoteSuccess }
    );
  };

  const getPercent = (votes: number): number => {
    if (totalVotes === 0) return 0;
    return Math.round((votes / totalVotes) * 100);
  };

  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-yellow-900/50 bg-yellow-900/20' : 'border-yellow-100 bg-yellow-50/50'} p-3 text-[13px]`}>
      {post.title && <p className={`font-semibold text-[15px] mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{post.title}</p>}

      <div className="space-y-2">
        {displayOptions.map((option, i) => {
          const percent = getPercent(option.votes);
          const isSelected = option.has_voted;
          const winning = option.votes === maxVotes && maxVotes > 0;

          return (
            <button
              key={i}
              onClick={() => handleVote(i)}
              disabled={isPending}
              className={`relative w-full rounded-lg overflow-hidden text-left transition-all border ${
                hasVoted ? 'cursor-default' : 'cursor-pointer hover:border-yellow-400 dark:hover:border-yellow-600'
              } ${isSelected ? 'border-yellow-400 dark:border-yellow-600 bg-white dark:bg-neutral-800' : `border-yellow-200 dark:border-yellow-800 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}`}
            >
              {hasVoted && (
                <div
                  className={`absolute inset-0 transition-all duration-700 ease-out rounded-lg ${
                    isSelected ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-50 dark:bg-neutral-700/50'
                  }`}
                  style={{ width: `${percent}%` }}
                />
              )}
              <div className="relative flex items-center justify-between px-3 py-2.5 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      isSelected
                        ? 'border-yellow-500 bg-yellow-500'
                        : hasVoted
                        ? 'border-gray-300 dark:border-gray-600'
                        : 'border-yellow-400 dark:border-yellow-600'
                    }`}
                  >
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </span>
                  <span className={`font-medium truncate ${isSelected ? 'text-yellow-800 dark:text-yellow-300' : `text-gray-700 dark:text-gray-300`}`}>
                    {option.label}
                  </span>
                  {hasVoted && winning && (
                    <span className="text-[10px] font-semibold text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      Leading
                    </span>
                  )}
                </div>
                {hasVoted && (
                  <span className={`text-[13px] font-semibold flex-shrink-0 ${isSelected ? 'text-yellow-700 dark:text-yellow-300' : 'text-gray-500 dark:text-gray-400'}`}>
                    {percent}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {totalVotes === 0 ? 'No votes yet — be the first!' : `${totalVotes} vote${totalVotes !== 1 ? 's' : ''}`}
        {hasVoted && <span className="ml-1 text-yellow-500 dark:text-yellow-400 font-medium">· You voted</span>}
        {isPending && <span className="ml-1 text-yellow-400 dark:text-yellow-500 font-medium animate-pulse">· Saving...</span>}
      </div>
    </div>
  );
};

const TaskDetails = ({ post }: { post: any }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-orange-900/50 bg-orange-900/20' : 'border-orange-100 bg-orange-50/50'} p-3 space-y-1.5 text-[13px]`}>
      {post.title && <p className={`font-semibold text-[15px] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{post.title}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
        {post.task_type && (
          <span className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
            {post.task_type}
          </span>
        )}
        {post.amount > 0 && (
          <span className="flex items-center gap-1 font-semibold text-orange-700 dark:text-orange-400">
            ₦{post.amount.toLocaleString()}
          </span>
        )}
        {post.start_date && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formatDate(post.start_date)}
          </span>
        )}
        {post.deadline_date && (
          <span className="flex items-center gap-1 text-red-500 dark:text-red-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Due {formatDate(post.deadline_date)}
          </span>
        )}
      </div>
    </div>
  );
};

const RentDetails = ({ post }: { post: any }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-rose-900/50 bg-rose-900/20' : 'border-rose-100 bg-rose-50/50'} p-3 space-y-1.5 text-[13px]`}>
      {post.title && <p className={`font-semibold text-[15px] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{post.title}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
        {post.location && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {post.location}
          </span>
        )}
        {post.amount > 0 && (
          <span className="flex items-center gap-1 font-semibold text-rose-700 dark:text-rose-400">
            ₦{post.amount.toLocaleString()}<span className="font-normal text-gray-500 dark:text-gray-400">/yr</span>
          </span>
        )}
      </div>
    </div>
  );
};

const RideDetails = ({ post }: { post: any }) => {
  const { isDarkMode } = useTheme();
  
  const formatTime12hr = (time?: string): string => {
    if (!time) return '';
    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = minuteStr ? parseInt(minuteStr, 10) : 0;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const isDriver = post.ride_user_type === 'driver';

  return (
    <div className={`mb-4 rounded-md border ${isDarkMode ? 'border-cyan-900/50 bg-cyan-900/20' : 'border-cyan-100 bg-cyan-50/50'} p-3 space-y-2 text-[13px]`}>
      <div className="flex items-center gap-2">
        {post.ride_user_type && (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              isDriver 
                ? 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300' 
                : 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300'
            }`}
          >
            {isDriver ? '🚗 Offering Ride' : '🙋 Needs Ride'}
          </span>
        )}
        {post.amount > 0 && (
          <span className="font-semibold text-cyan-700 dark:text-cyan-400">
            {post.currency}{post.amount.toLocaleString()}
            {isDriver && (
              <span className="font-normal text-gray-500 dark:text-gray-400 text-[11px] ml-1">available</span>
            )}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1 text-gray-600 dark:text-gray-300">
        {post.location && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
            <span className="font-medium text-gray-700 dark:text-gray-300">From:</span> {post.location}
          </span>
        )}
        {post.destination && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
            <span className="font-medium text-gray-700 dark:text-gray-300">To:</span> {post.destination}
          </span>
        )}
        {(post.start_date || post.trip_time) && (
          <span className="flex items-center gap-1.5 text-cyan-700 dark:text-cyan-400 font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {post.start_date && formatDate(post.start_date)}
            {post.start_date && post.trip_time && ' · '}
            {post.trip_time && formatTime12hr(post.trip_time)}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── renderTypeDetails helper ─────────────────────────────────────────────────
const renderTypeDetails = (post: any, onVoteSuccess: () => void) => {
  switch (post?.post_type) {
    case 'job':    return <JobDetails post={post} />;
    case 'market': return <MarketDetails post={post} />;
    case 'events': return <EventDetails post={post} />;
    case 'poll':   return <PollDetails post={post} onVoteSuccess={onVoteSuccess} />;
    case 'task':   return <TaskDetails post={post} />;
    case 'rent':   return <RentDetails post={post} />;
    case 'rides':  return <RideDetails post={post} />;
    default:       return null;
  }
};

// ─── Reply bubble border-radius logic ────────────────────────────────────────
const getReplyBorderRadius = (index: number, total: number): string => {
  if (total === 1) return 'rounded-bl-xl rounded-tr-lg rounded-br-xl';
  if (index === 0) return 'rounded-tl-xl rounded-tr-xl rounded-br-none rounded-bl-none';
  if (index === total - 1) return 'rounded-bl-xl rounded-br-xl rounded-tr-none rounded-tl-none';
  return 'rounded-none';
};

// ─── Individual Reply Component with delete only ───────────────────────────────
const ReplyItem = ({ 
  reply, 
  index, 
  total, 
  currentUser, 
  onReplyClick,
  onDeleteReply
}: { 
  reply: any; 
  index: number; 
  total: number;
  currentUser: any;
  onReplyClick: (id: string, username: string) => void;
  onDeleteReply: (replyId: string) => Promise<void>;
}) => {
  const { isDarkMode } = useTheme();
  const radius = getReplyBorderRadius(index, total);
  const isOwner = currentUser?.username === reply?.creator?.username;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDeleteReply(reply.comment_id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={`flex gap-2 items-start p-1.5 ${radius} ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="shrink-0">
          {reply?.creator?.profile_picture_url ? (
            <img
              src={reply.creator.profile_picture_url}
              alt="Avatar"
              className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-700 shadow-sm"
            />
          ) : (
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex justify-center items-center shadow-sm">
              <span className="text-xs font-semibold text-white">
                {reply?.creator?.username?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-1.5 mb-0.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {reply?.creator?.username}
              </p>
              {reply?.creator?.is_verified && (
                <img src={Verify} alt="verified" className="h-3 w-3" />
              )}
              <span className="text-[9px] text-gray-400 dark:text-gray-500">•</span>
              <p className="text-[9px] text-gray-400 dark:text-gray-500">{reply?.created}</p>
            </div>
            {isOwner && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Delete reply"
                title="Delete reply"
              >
                <LuTrash2 className="size-3 text-red-500" />
              </button>
            )}
          </div>
          <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {reply?.comment}
          </p>
          <button
            type="button"
            onClick={() => onReplyClick(reply.comment_id, reply?.creator?.username)}
            className="text-[10px] font-medium text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors mt-1"
          >
            Reply
          </button>
        </div>
      </div>

      {/* Delete Modal for Reply */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

// ─── Replies toggle section with delete only ───────────────────────────────────
const RepliesSection = ({
  replies,
  onReply,
  currentUser,
  onDeleteReply,
}: {
  replies: any[];
  onReply: (id: string, username: string) => void;
  currentUser: any;
  onDeleteReply: (replyId: string) => Promise<void>;
}) => {
  const [showReplies, setShowReplies] = useState(false);
  const count = replies.length;

  return (
    <div className="mt-2 ml-1">
      <button
        type="button"
        onClick={() => setShowReplies((prev) => !prev)}
        className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors mb-2"
      >
        {showReplies ? (
          <>
            <TbChevronUp className="size-3.5" />
            Hide {count} {count === 1 ? 'reply' : 'replies'}
          </>
        ) : (
          <>
            <TbChevronDown className="size-3.5" />
            {count} {count === 1 ? 'reply' : 'replies'}
          </>
        )}
      </button>

      {showReplies && (
        <div className="flex flex-col gap-1.5">
          {replies.map((reply: any, index: number) => (
            <ReplyItem
              key={reply.comment_id || index}
              reply={reply}
              index={index}
              total={count}
              currentUser={currentUser}
              onReplyClick={onReply}
              onDeleteReply={onDeleteReply}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Comment Component with delete only ───────────────────────────────────
const CommentItem = ({ 
  comment, 
  currentUser, 
  onReply,
  onDeleteComment,
  onDeleteReply,
}: { 
  comment: any; 
  currentUser: any;
  onReply: (id: string, username: string) => void;
  onDeleteComment: (commentId: string) => Promise<void>;
  onDeleteReply: (replyId: string) => Promise<void>;
}) => {
  const { isDarkMode } = useTheme();
  const isOwner = currentUser?.username === comment?.creator?.username;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDeleteComment(comment.comment_id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex gap-2 mb-5 items-start">
        <div className="shrink-0">
          {comment?.creator?.profile_picture_url ? (
            <img
              src={comment.creator.profile_picture_url}
              alt="Avatar"
              className="w-10 h-10 rounded-full border-2 border-white dark:border-gray-700 shadow-sm"
            />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex justify-center items-center shadow-sm">
              <span className="text-sm font-semibold text-white">
                {comment?.creator?.username?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        <div className="flex-1">
          <div className={`rounded-bl-xl p-3 rounded-r-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {comment?.creator?.username}
                </p>
                {comment?.creator?.is_verified && (
                  <img src={Verify} alt="verified" className="h-3 w-3" />
                )}
                <span className="text-[10px] text-gray-400 dark:text-gray-500">•</span>
                <p className="text-[10px] text-gray-400 dark:text-gray-500">{comment?.created}</p>
              </div>
              {isOwner && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Delete comment"
                  title="Delete comment"
                >
                  <LuTrash2 className="size-3.5 text-red-500" />
                </button>
              )}
            </div>
            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {comment?.comment}
            </p>
          </div>

          <div className="flex items-center gap-4 mt-1 ml-1">
            <button
              type="button"
              onClick={() => onReply(comment?.comment_id, comment?.creator?.username)}
              className="text-xs font-medium text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              Reply
            </button>
          </div>

          {comment.replies && comment.replies.length > 0 && (
            <RepliesSection
              replies={comment.replies}
              onReply={onReply}
              currentUser={currentUser}
              onDeleteReply={onDeleteReply}
            />
          )}
        </div>
      </div>

      {/* Delete Modal for Comment */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ViewPost = () => {
  document.body.classList.remove('overflow-hidden');
  const { id } = useParams();
  const [post, setPost] = useState<any>({});
  const [commentMssg, setComment] = useState<any>('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [parentId, setParentId] = useState<any>(null);
  const [replyto, setReplyto] = useState<any>(null);
  const { isDarkMode } = useTheme();
  const currentUser = getUser();

  const comments = useFeedComments();
  const post_comment = usePostComment();
  const delete_comment = useDeleteComment(); // Initialize delete hook

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!!textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 4 + 'px';
    }
  }, [commentMssg]);

  useEffect(() => {
    if (id) {
      getThisPost(id);
      comments.mutate(Number(id));
    }
    // eslint-disable-next-line
  }, [id]);

  const getThisPost = async (id: any) => {
    setLoading(true);
    const feed_info = await getPostById(Number(id));
    setPost(feed_info?.data);
    setLoading(false);
  };

  const handleVoteSuccess = () => {
    if (id) getThisPost(id);
  };

  const sendComment = async () => {
    setComment('');
    await post_comment.mutate(
      { id: Number(id), commentMssg, parentId },
      {
        onSuccess: () => {
          comments.mutate(Number(id));
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          setReplyto(null);
          setParentId(null);
        },
        onError: (error) => {
          console.error('Error posting comment:', error);
        },
      },
    );
  };

  // Delete comment using the hook
  const handleDeleteComment = async (commentId: string) => {
    await delete_comment.mutateAsync(
      { commentId: Number(commentId) },
      {
        onSuccess: () => {
          comments.mutate(Number(id));
        },
        onError: (error) => {
          console.error('Error deleting comment:', error);
        },
      }
    );
  };

  // Delete reply using the same hook (since replies are also comments)
  const handleDeleteReply = async (replyId: string) => {
    await delete_comment.mutateAsync(
      { commentId: Number(replyId) },
      {
        onSuccess: () => {
          comments.mutate(Number(id));
        },
        onError: (error) => {
          console.error('Error deleting reply:', error);
        },
      }
    );
  };

  const isOnlyLink = (str: string): boolean => {
    const urlPattern =
      /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return urlPattern.test(str);
  };

  const focusInput = () => {
    const input = document.getElementById('myInput');
    if (input) input.focus();
  };

  const typeConfig = post?.post_type ? POST_TYPE_CONFIG[post.post_type] : null;
  const showBadge = typeConfig && post.post_type !== 'tweet';

  // Show skeleton while loading
  if (loading) {
    return (
      <section className={`m-0 p-0 min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        <div className="fixed top-0 right-0 left-0 w-full z-50">
          <TopBar title="Post" link={SITELINK.HOME} />
        </div>
        <div className="mt-12">
          <SkeletonFeed count={1} variant="post" />
        </div>
      </section>
    );
  }

  return (
    <section className={`m-0 p-0 min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      {/* top nav */}
      <div className="fixed top-0 right-0 left-0 w-full z-50">
        <TopBar title="Post" link={SITELINK.HOME} />
      </div>

      {/* content */}
      {post?.creator?.username && (
        <div className={`mt-12 mb-15 ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-300'} border`}>
          {/* creator header */}
          <div className="flex gap-2 justify-between p-3 pb-0">
            <div className="flex gap-2">
              <div>
                <Link to={`${SITELINK.MY_PROFILE}/${post?.creator?.username}`}>
                  {post?.creator?.profile_picture_url ? (
                    <div className="flex-grow flex flex-col items-center">
                      <div className="overflow-hidden flex justify-center">
                        <LazyLoad height={200} offset={1000} once>
                          <img
                            alt="profile"
                            src={post?.creator?.profile_picture_url}
                            className="object-cover rounded-full w-[27px] h-[27px] bg-gray-200 dark:bg-gray-700 shadow-sm overflow-hidden"
                          />
                        </LazyLoad>
                      </div>
                    </div>
                  ) : (
                    <span
                      className="block w-[27px] h-[27px] rounded-full border border-green-100 dark:border-green-800"
                      style={{ background: generateRandomHexColor() }}
                    >
                      <h3 className="text-white uppercase text-[1rem] font-[500] relative top-[1px] left-1.5">
                        {getFirstLetters(post?.creator?.username ?? 'C')}
                      </h3>
                    </span>
                  )}
                </Link>
              </div>

              <div>
                <p className="text-xs font-semibold flex gap-1 items-center dark:text-white">
                  <span>{post?.creator?.username ?? 'Anonymous'}</span>
                  {post?.creator?.is_verified && (
                    <img src={Verify} alt="" className="h-[12px]" />
                  )}
                  {showBadge && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeConfig.bg} ${typeConfig.text}`}>
                      {typeConfig.icon}
                      {typeConfig.label}
                    </span>
                  )}
                </p>
                <div className="flex gap-2 relative items-center">
                  <p className="text-[8px] font-medium mb-0">
                    <Link to={`${SITELINK.COMMUNITIES}/${post?.community}`} className="text-gray-500 dark:text-gray-400">
                      {post?.community}
                    </Link>
                  </p>
                  <p className="text-[8px] text-gray-400 font-medium mb-0 ml-1">{post?.created_on}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-end w-full hidden">
                <button className="flex justify-center w-full">
                  <LuMoreHorizontal className="text-xl text-gray-800 dark:text-gray-200" />
                </button>
              </div>
              {post?.is_boosted && (
                <button className="flex justify-end rounded-lg bg-white dark:bg-neutral-700 border border-gray-300 dark:border-gray-600 font-bold text-gray-500 dark:text-gray-300 px-1 border text-[8px]">
                  Sponsored
                </button>
              )}
            </div>
          </div>

          {/* post body */}
          <div className="px-3 mb-3 mt-3">
            {/* Type-specific details */}
            {renderTypeDetails(post, handleVoteSuccess)}

            {/* Text content */}
            {post?.content && post.content !== 'undefined' && (
              isOnlyLink(post.content) ? (
                <div>
                  <LinkPreview url={post.content} />
                </div>
              ) : (
                <div className={`text-[0.9rem] font-medium break-all mt-1 ${isDarkMode ? 'text-gray-200' : 'text-black'}`}>
                  <LinkPreview url={post.content} />
                  {post.content}
                </div>
              )
            )}
          </div>

          {/* audio */}
          {post?.audio?.length > 0 && (
            <div className="px-3 w-full pb-3">
              <audio controls src={post?.audio[0]} className="w-full custom-audio-player"></audio>
            </div>
          )}

          {/* images */}
          {post?.images?.length > 0 && (
            <div
              className="relative flex justify-center items-center overflow-hidden bg-cover bg-center"
              style={{
                gridTemplateColumns: `repeat(${Math.min(post?.images?.length, 4)}, minmax(0, 1fr))`,
              }}
            >
              {post?.images?.map((image: string, index: number) => (
                <LazyLoad height={200} offset={1000} once key={index}>
                  <div
                    className="absolute inset-0 bg-cover bg-center blur-3xl"
                    style={{ backgroundImage: `url(${image})` }}
                  ></div>
                  <img src={image} className="relative w-full z-10" alt={`post_image ${index}`} />
                </LazyLoad>
              ))}
            </div>
          )}

          {/* action buttons (hidden) */}
          <div className="px-3 pb-3 border-t border-gray-50 dark:border-gray-700 mb-24 hidden">
            <div className="flex justify-between gap-2 mt-2 font-medium text-[12px]">
              <button className={`flex items-center rounded-full px-3 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="like-button">
                  <div className="heart-bg">
                    <div className={`heart-icon ${post?.reacted_to_post && 'liked'}`}></div>
                  </div>
                </div>
                {post?.reactions > 0 && <span className="pr-1 dark:text-white">{post?.reactions}</span>}
                <span className="pr-1 dark:text-white">Like{post?.reactions > 1 ? 's' : ''}</span>
              </button>

              <Link
                to={SITELINK.VIEW_POSTS + '/' + post.post_id}
                className={`flex items-center rounded-full px-3 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
              >
                <TbMessageCircle className="text-[1.1rem] text-gray-500 dark:text-gray-400" />
                <span className="pr-1 dark:text-white">{post?.comments > 1 && post?.comments}</span>
                <span className="pr-1 dark:text-white">Comment{post?.comments > 1 ? 's' : ''}</span>
              </Link>

              <button className={`flex items-center rounded-full px-3 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <PiShareFatDuotone className="text-[1.1rem] text-gray-500 dark:text-gray-400" />
                <span className="pr-1 dark:text-white">Share</span>
              </button>
            </div>
          </div>

          {/* ── Comment Section with Delete Only ── */}
          <div className={`comment-section p-3 rounded-lg mb-[170px] ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
            {comments?.data?.data?.length ? (
              comments.data.data.map((item: any, index: number) => (
                <CommentItem
                  key={item.comment_id || index}
                  comment={item}
                  currentUser={currentUser}
                  onReply={(id: string, username: string) => {
                    setParentId(id);
                    setReplyto('Replying to ' + username);
                    focusInput();
                  }}
                  onDeleteComment={handleDeleteComment}
                  onDeleteReply={handleDeleteReply}
                />
              ))
            ) : (
              <p className="text-center text-gray-400 dark:text-gray-500 py-8 font-bold text-sm">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Comment input with dark mode */}
      <div className={`shadow-2xl w-full border-t z-50 px-3 pb-3 fixed bottom-0 ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="mt-3 font-medium text-[12px]">
          <div className="relative w-full">
            <textarea
              value={commentMssg}
              onChange={(e) => setComment(e.target.value)}
              placeholder={replyto ?? 'write a comment'}
              className={`text-sm border rounded-xl p-3 pr-7 w-full focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-400 dark:focus:border-blue-600 transition-all ${isDarkMode ? 'bg-neutral-700 border-neutral-600 text-white placeholder-gray-400' : 'bg-white border-gray-200'}`}
              style={{
                width: '100%',
                minHeight: '34px',
                maxHeight: '200px',
                overflow: 'scroll',
                resize: 'none',
              }}
              id="myInput"
              rows={1}
              ref={textareaRef}
            ></textarea>

            <button
              onClick={sendComment}
              disabled={!commentMssg.trim()}
              className={`text-3xl absolute text-semibold bottom-[15px] right-[10px] transition-colors ${
                commentMssg.trim() 
                  ? 'text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300' 
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
            >
              <RiSendPlane2Fill className="size-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ViewPost;